/** Minimal workspace member profile used to render artifact creators. */
export type ArtifactAuthorProfile = {
  id: string;
  name: string;
  picture: string | null;
};

type OrganizationUserEntry = {
  user: {
    id: string;
    name: string;
    picture: string | null;
  };
};

/** Build a lookup table from one Organization.users payload; never infer names from IDs. */
export function buildAuthorProfileMap(
  orgUsers: OrganizationUserEntry[] | null | undefined,
): Map<string, ArtifactAuthorProfile> {
  const map = new Map<string, ArtifactAuthorProfile>();
  for (const entry of orgUsers ?? []) {
    const user = entry.user;
    if (!user?.id) continue;
    map.set(user.id, {
      id: user.id,
      name: user.name,
      picture: user.picture,
    });
  }
  return map;
}

/** Match artifact.createdByID exactly; unavailable members degrade to a neutral label. */
export function resolveArtifactAuthor(
  createdByID: string,
  profileMap: Map<string, ArtifactAuthorProfile>,
  unknownName: string,
): ArtifactAuthorProfile {
  const profile = profileMap.get(createdByID);
  if (profile?.name.trim()) {
    return profile;
  }
  if (profile) {
    return { ...profile, name: unknownName };
  }
  return {
    id: createdByID,
    name: unknownName,
    picture: null,
  };
}

/** Avatar fallback initials for compact artifact metadata rows. */
export function artifactAuthorInitials(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return "?";

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = Array.from(parts[0] ?? "")[0] ?? "";
    const second = Array.from(parts[1] ?? "")[0] ?? "";
    const initials = `${first}${second}`.toUpperCase();
    return initials || "?";
  }

  const initials = Array.from(parts[0] ?? trimmed)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return initials || "?";
}
